import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, Sparkles, Copy, Linkedin, Mail, ExternalLink, ChevronRight,
  Loader2, X, Check, Search, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

const STATUS = {
  to_contact: { label: "À contacter", color: "text-zinc-400", bg: "bg-zinc-500/10", next: "contacted" },
  contacted: { label: "Contacté", color: "text-blue-400", bg: "bg-blue-500/10", next: "replied" },
  replied: { label: "Répondu", color: "text-amber-400", bg: "bg-amber-500/10", next: "meeting" },
  meeting: { label: "RDV pris", color: "text-fuchsia-400", bg: "bg-fuchsia-500/10", next: "pilote" },
  pilote: { label: "Pilote 🎉", color: "text-emerald-400", bg: "bg-emerald-500/10", next: null },
  lost: { label: "Perdu", color: "text-red-400", bg: "bg-red-500/10", next: null },
};

const STATUS_ORDER = ["to_contact", "contacted", "replied", "meeting", "pilote", "lost"];

export default function Prospects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [opened, setOpened] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prospects");
      setItems(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    let f = items;
    if (filter !== "all") f = f.filter((p) => p.status === filter);
    if (q.trim()) {
      const s = q.toLowerCase();
      f = f.filter((p) => `${p.full_name} ${p.company} ${p.role} ${p.email}`.toLowerCase().includes(s));
    }
    return f;
  }, [items, filter, q]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    STATUS_ORDER.forEach((s) => { c[s] = items.filter((p) => p.status === s).length; });
    return c;
  }, [items]);

  const handleStatusChange = async (id, newStatus) => {
    await api.patch(`/prospects/${id}`, { status: newStatus });
    refresh();
    if (opened?.id === id) {
      const { data } = await api.get("/prospects");
      const fresh = data.find((p) => p.id === id);
      setOpened(fresh);
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (!confirm("Supprimer ce prospect ?")) return;
    await api.delete(`/prospects/${id}`);
    refresh();
    if (opened?.id === id) setOpened(null);
  };

  return (
    <ConsoleShell
      title="Prospects"
      breadcrumb={["GABRYX", "Acquisition", "Prospects"]}
      actions={
        <>
          <button onClick={refresh} className="text-xs border border-white/15 px-3 py-1.5 hover:bg-white/5 transition flex items-center gap-1.5" data-testid="prospects-refresh">
            <RefreshCw className="w-3 h-3" /> Actualiser
          </button>
          <button onClick={() => setShowAdd(true)} className="text-xs bg-white text-black px-3 py-1.5 hover:bg-zinc-200 transition flex items-center gap-1.5 font-medium" data-testid="prospects-add-btn">
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </>
      }
    >
      <div className="px-5 lg:px-8 py-6 space-y-4 max-w-[1600px]">
        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <FilterChip label="Tous" count={counts.all} active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all" />
            {STATUS_ORDER.map((s) => (
              <FilterChip
                key={s}
                label={STATUS[s].label}
                count={counts[s]}
                active={filter === s}
                onClick={() => setFilter(s)}
                color={STATUS[s].color}
                testid={`filter-${s}`}
              />
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher nom, société…"
              data-testid="prospects-search"
              className="bg-[#0f0f0f] border border-white/15 pl-8 pr-3 py-1.5 text-xs w-64 focus:border-white/40 outline-none font-mono"
            />
          </div>
        </div>

        {/* Table */}
        <div className="border border-white/10 bg-[#0f0f0f]">
          {loading ? (
            <div className="p-12 text-center text-zinc-500 text-sm">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Sparkles className="w-6 h-6 mx-auto mb-3 text-zinc-600" />
              <div className="font-display text-base mb-1">{items.length === 0 ? "Aucun prospect" : "Aucun résultat"}</div>
              <p className="text-xs text-zinc-500 mb-4">
                {items.length === 0
                  ? "Ajoutez votre premier prospect — CLARA générera les messages LinkedIn + email pour vous."
                  : "Ajustez vos filtres ou la recherche."}
              </p>
              {items.length === 0 && (
                <button onClick={() => setShowAdd(true)} className="text-xs bg-white text-black px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-zinc-200 transition">
                  <Plus className="w-3.5 h-3.5" /> Ajouter un prospect
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <table className="w-full text-sm hidden md:table" data-testid="prospects-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
                    <th className="text-left px-5 py-3 font-normal">Prospect</th>
                    <th className="text-left px-3 py-3 font-normal">Société</th>
                    <th className="text-left px-3 py-3 font-normal">Rôle</th>
                    <th className="text-center px-3 py-3 font-normal">Statut</th>
                    <th className="text-center px-3 py-3 font-normal">CLARA</th>
                    <th className="text-right px-5 py-3 font-normal">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const s = STATUS[p.status] || STATUS.to_contact;
                    return (
                      <tr
                        key={p.id}
                        onClick={() => setOpened(p)}
                        className="border-b border-white/5 hover:bg-white/5 transition cursor-pointer"
                        data-testid={`prospect-row-${p.id}`}
                      >
                        <td className="px-5 py-3">
                          <div className="font-medium">{p.full_name}</div>
                          {p.email && <div className="text-[11px] text-zinc-500 font-mono">{p.email}</div>}
                        </td>
                        <td className="px-3 py-3 text-zinc-300">{p.company || "—"}</td>
                        <td className="px-3 py-3 text-zinc-400 text-xs">{p.role || "—"}</td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-0.5 ${s.bg} ${s.color}`}>
                            {s.label}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {p.outreach ? <Check className="w-3.5 h-3.5 text-emerald-400 mx-auto" /> : <span className="text-zinc-600">—</span>}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={(e) => handleDelete(p.id, e)}
                            className="text-zinc-500 hover:text-red-400 p-1"
                            data-testid={`prospect-delete-${p.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Mobile card list */}
              <div className="md:hidden divide-y divide-white/5" data-testid="prospects-cards">
                {filtered.map((p) => {
                  const s = STATUS[p.status] || STATUS.to_contact;
                  return (
                    <div
                      key={p.id}
                      onClick={() => setOpened(p)}
                      className="p-4 hover:bg-white/[0.03] transition cursor-pointer"
                      data-testid={`prospect-card-${p.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="font-medium text-sm truncate">{p.full_name}</div>
                          {p.company && (
                            <div className="text-xs text-zinc-400 truncate">{p.company}</div>
                          )}
                          {p.role && (
                            <div className="text-[10px] text-zinc-500 uppercase tracking-wider truncate">{p.role}</div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                          <span className={`text-[9px] font-mono uppercase tracking-[0.1em] px-2 py-0.5 ${s.bg} ${s.color}`}>
                            {s.label}
                          </span>
                          {p.outreach && (
                            <span className="text-[9px] text-emerald-400 inline-flex items-center gap-1">
                              <Check className="w-2.5 h-2.5" />
                              Clara OK
                            </span>
                          )}
                        </div>
                      </div>
                      {p.email && (
                        <div className="text-[10px] text-zinc-500 font-mono mt-2 truncate">{p.email}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="text-[10px] text-zinc-600 font-mono">
          {filtered.length} prospect{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur {items.length}
        </div>
      </div>

      {showAdd && <AddProspectModal onClose={() => setShowAdd(false)} onSaved={() => { refresh(); setShowAdd(false); }} />}
      {opened && (
        <ProspectDrawer
          prospect={opened}
          onClose={() => setOpened(null)}
          onStatusChange={handleStatusChange}
          onRefresh={async () => {
            const { data } = await api.get("/prospects");
            setItems(data);
            const fresh = data.find((p) => p.id === opened.id);
            if (fresh) setOpened(fresh);
          }}
        />
      )}
    </ConsoleShell>
  );
}

function FilterChip({ label, count, active, onClick, color, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1.5 border transition ${active ? "bg-white text-black border-white" : `border-white/15 hover:bg-white/5 ${color || "text-zinc-400 hover:text-white"}`}`}
    >
      {label}
      <span className="ml-1.5 font-mono opacity-60">{count}</span>
    </button>
  );
}

function AddProspectModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ full_name: "", company: "", role: "", linkedin_url: "", email: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Nom obligatoire");
    setSaving(true);
    try {
      await api.post("/prospects", form);
      toast.success("Prospect ajouté");
      onSaved();
    } catch (err) {
      toast.error("Erreur : " + (err?.response?.data?.detail || ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#0f0f0f] border border-white/10 w-full max-w-lg" data-testid="add-prospect-modal">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Acquisition</div>
            <h2 className="font-display text-xl font-semibold">Nouveau prospect</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Nom complet *" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="form-fullname" required />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Société" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="form-company" />
            <Field label="Rôle" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="form-role" placeholder="Ex: Coordonnateur SPS" />
          </div>
          <Field label="URL LinkedIn" value={form.linkedin_url} onChange={(v) => setForm({ ...form, linkedin_url: v })} testid="form-linkedin" placeholder="https://linkedin.com/in/…" />
          <Field label="Email pro" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="form-email" placeholder="prenom@societe.fr" />
          <Field label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="form-notes" textarea placeholder="Contexte, signaux d'intérêt, source…" />
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 hover:bg-white/5">Annuler</button>
          <button type="submit" disabled={saving} data-testid="form-submit" className="text-xs bg-white text-black px-4 py-2 font-medium hover:bg-zinc-200 disabled:opacity-50 flex items-center gap-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Ajouter
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, textarea, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1.5">{label}</label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          rows={3}
          placeholder={placeholder}
          className="w-full bg-[#0a0a0a] border border-white/15 focus:border-white/40 outline-none px-3 py-2 text-sm font-mono resize-none"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
          required={required}
          className="w-full bg-[#0a0a0a] border border-white/15 focus:border-white/40 outline-none px-3 py-2 text-sm font-mono"
        />
      )}
    </div>
  );
}

function ProspectDrawer({ prospect, onClose, onStatusChange, onRefresh }) {
  const [generating, setGenerating] = useState(false);
  const s = STATUS[prospect.status] || STATUS.to_contact;

  const generate = async () => {
    setGenerating(true);
    toast.info("CLARA rédige tes messages…");
    try {
      await api.post(`/prospects/${prospect.id}/generate-outreach`);
      toast.success("Messages générés ✨");
      onRefresh();
    } catch (e) {
      toast.error("Erreur génération");
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border-l border-white/10 w-full max-w-2xl h-full overflow-y-auto" data-testid="prospect-detail">
        <div className="sticky top-0 glass border-b border-white/10 p-5 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Prospect</div>
            <h2 className="font-display text-xl font-semibold truncate">{prospect.full_name}</h2>
            <div className="text-xs text-zinc-500 mt-0.5">{prospect.company || "—"} · {prospect.role || "—"}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Status pipeline */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Pipeline</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
              {STATUS_ORDER.map((st) => {
                const stInfo = STATUS[st];
                const active = prospect.status === st;
                return (
                  <button
                    key={st}
                    onClick={() => onStatusChange(prospect.id, st)}
                    data-testid={`status-set-${st}`}
                    className={`text-[10px] uppercase tracking-[0.1em] py-1.5 transition ${active ? `${stInfo.bg} ${stInfo.color} font-medium` : "text-zinc-500 hover:bg-white/5"}`}
                  >
                    {stInfo.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick links */}
          <div className="flex flex-wrap gap-2">
            {prospect.linkedin_url && (
              <a href={prospect.linkedin_url} target="_blank" rel="noreferrer" className="text-xs border border-white/15 px-3 py-1.5 hover:bg-white/5 inline-flex items-center gap-1.5">
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {prospect.email && (
              <>
                <a href={`mailto:${prospect.email}`} className="text-xs border border-white/15 px-3 py-1.5 hover:bg-white/5 inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> {prospect.email}
                </a>
                <ClaraEmailButton prospect={prospect} />
              </>
            )}
          </div>

          {prospect.notes && (
            <div className="bg-[#0f0f0f] border border-white/10 p-3 text-xs text-zinc-300 whitespace-pre-wrap font-mono">
              {prospect.notes}
            </div>
          )}

          {/* Outreach assets */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">CLARA outreach</div>
                <div className="font-display text-base">Messages personnalisés</div>
              </div>
              <button
                onClick={generate}
                disabled={generating}
                data-testid="generate-outreach-btn"
                className="text-xs bg-pink-500/10 border border-pink-500/30 text-pink-400 px-3 py-1.5 hover:bg-pink-500/20 transition flex items-center gap-1.5 disabled:opacity-50"
              >
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {prospect.outreach ? "Régénérer" : "Faire générer par CLARA"}
              </button>
            </div>

            {prospect.outreach ? (
              <>
                <OutreachBlock
                  icon={Linkedin}
                  title="LinkedIn — message de connexion"
                  content={prospect.outreach.linkedin_invite}
                  onCopy={() => copy(prospect.outreach.linkedin_invite, "Message LinkedIn")}
                  color="#0A66C2"
                  testid="outreach-linkedin-invite"
                />
                <OutreachBlock
                  icon={Linkedin}
                  title="LinkedIn — follow-up post-connexion"
                  content={prospect.outreach.linkedin_followup}
                  onCopy={() => copy(prospect.outreach.linkedin_followup, "Follow-up")}
                  color="#0A66C2"
                  testid="outreach-linkedin-followup"
                />
                <OutreachBlock
                  icon={Mail}
                  title="Cold email"
                  content={prospect.outreach.cold_email}
                  onCopy={() => copy(prospect.outreach.cold_email, "Email")}
                  color="#10B981"
                  testid="outreach-cold-email"
                />
                <div className="text-[10px] text-zinc-600 font-mono">
                  Généré le {new Date(prospect.outreach.generated_at).toLocaleString("fr-FR")}
                </div>
              </>
            ) : (
              <div className="bg-[#0f0f0f] border border-dashed border-white/10 p-8 text-center">
                <Sparkles className="w-5 h-5 mx-auto mb-2 text-zinc-600" />
                <div className="text-sm">Pas encore de messages générés</div>
                <p className="text-xs text-zinc-500 mt-1">Cliquez sur «&nbsp;Faire générer par CLARA&nbsp;» — ~10 secondes</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OutreachBlock({ icon: Icon, title, content, onCopy, color, testid }) {
  if (!content) return null;
  return (
    <div className="bg-[#0f0f0f] border-l-2 p-4" style={{ borderLeftColor: color }} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <div className="text-xs font-medium">{title}</div>
        </div>
        <button onClick={onCopy} className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1">
          <Copy className="w-3 h-3" /> Copier
        </button>
      </div>
      <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed font-mono text-[13px]">{content}</div>
    </div>
  );
}

function ClaraEmailButton({ prospect }) {
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState("");
  const [draft, setDraft] = useState(null); // {id, subject, body, to}
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const ask = async () => {
    if (!intent.trim()) { toast.error("Précise l'intention du mail"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/emails/clara-draft", {
        to: prospect.email,
        intent,
        prospect_id: prospect.id,
        tone: "professionnel et chaleureux",
      });
      setDraft(data);
      setEditedSubject(data.subject);
      setEditedBody(data.body);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CLARA n'a pas pu rédiger");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!confirm(`Envoyer cet email à ${prospect.email} ?`)) return;
    setBusy(true);
    try {
      await api.patch(`/emails/${draft.id}`, {
        to: prospect.email,
        subject: editedSubject,
        body: editedBody,
      });
      await api.post(`/emails/${draft.id}/send`);
      setSent(true);
      toast.success(`Email envoyé à ${prospect.email} ✨`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setOpen(false);
    setIntent("");
    setDraft(null);
    setSent(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`prospect-clara-email-${prospect.id}`}
        className="text-xs border border-pink-500/30 text-pink-300 hover:bg-pink-500/10 px-3 py-1.5 inline-flex items-center gap-1.5 transition"
      >
        <Sparkles className="w-3.5 h-3.5" /> Email via CLARA
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          data-testid="prospect-email-modal"
          onClick={(e) => { if (e.target === e.currentTarget) reset(); }}
        >
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="font-display font-semibold">
                Email à {prospect.full_name}
              </div>
              <button onClick={reset} className="text-zinc-500 hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-[60px_1fr] gap-2 text-xs">
                <div className="text-zinc-500 uppercase tracking-wider">De</div>
                <div className="font-mono text-zinc-300">gabryx@chantix.pro</div>
                <div className="text-zinc-500 uppercase tracking-wider">À</div>
                <div className="font-mono text-zinc-300">{prospect.email}</div>
              </div>

              {sent ? (
                <div className="border border-emerald-500/30 bg-emerald-500/5 p-6 text-center">
                  <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <div className="text-emerald-300">Envoyé à {prospect.email}</div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Le statut du prospect a été mis à jour à « Contacté ».
                  </p>
                  <button
                    onClick={reset}
                    className="mt-4 px-4 py-2 text-sm border border-white/15 hover:bg-white/5 transition"
                  >
                    Fermer
                  </button>
                </div>
              ) : !draft ? (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-pink-400 mb-2">
                      <Sparkles className="w-3 h-3 inline mr-1" />
                      Décris l&apos;intention du mail
                    </div>
                    <textarea
                      value={intent}
                      onChange={(e) => setIntent(e.target.value)}
                      placeholder={`ex: première prise de contact suite à notre rencontre au salon BTP — proposer une démo CHANTIX en 15 min`}
                      rows={4}
                      data-testid="prospect-email-intent"
                      className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none resize-none"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={reset}
                      className="px-4 py-2 text-sm border border-white/10 hover:bg-white/5 transition"
                      disabled={busy}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={ask}
                      disabled={busy || !intent.trim()}
                      data-testid="prospect-email-ask-clara"
                      className="px-4 py-2 text-sm bg-pink-500/10 border border-pink-500/30 text-pink-300 hover:bg-pink-500/20 transition disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {busy ? "CLARA rédige…" : "Demander à CLARA"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Objet</div>
                    <input
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      data-testid="prospect-email-subject"
                      className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Corps</div>
                    <textarea
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      rows={12}
                      data-testid="prospect-email-body"
                      className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none resize-y leading-relaxed"
                    />
                  </div>
                  <div className="flex justify-end gap-2 flex-wrap">
                    <button
                      onClick={() => setDraft(null)}
                      className="px-4 py-2 text-sm border border-white/10 hover:bg-white/5 transition"
                      disabled={busy}
                    >
                      ← Reformuler avec CLARA
                    </button>
                    <button
                      onClick={send}
                      disabled={busy}
                      data-testid="prospect-email-send"
                      className="px-4 py-2 text-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                      Envoyer
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

